import { forwardRef, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CHAT } from "@/constants/testIds/ashwin";
import { suggestions } from "@/data/resume";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip any markdown asterisks/underscores the model might emit
const stripMarkdown = (t) =>
  (t || "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/^\s*[-•*]\s+/gm, "")
    .replace(/`/g, "")
    .replace(/^#+\s*/gm, "");

const ChatWidget = forwardRef(function ChatWidget(
  { open, onOpenChange, initialPrompt, onPromptConsumed },
  ref
) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey — I'm Ashwin's AI agent. Ask me anything about his career, brands he's worked on, or how he thinks about strategy.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  useEffect(() => {
    if (open && initialPrompt) {
      const p = initialPrompt;
      onPromptConsumed?.();
      // give the panel a beat to mount before firing
      setTimeout(() => send(p), 150);
    }
  }, [open, initialPrompt]); // eslint-disable-line

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("stream_failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames split by blank line
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.role === "assistant") {
                  copy[copy.length - 1] = {
                    ...last,
                    content: stripMarkdown(last.content + evt.content),
                  };
                }
                return copy;
              });
            } else if (evt.type === "done") {
              if (evt.session_id) setSessionId(evt.session_id);
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content:
                    "Sorry — I couldn't reach the AI just now. Please try again or use the contact form.",
                };
                return copy;
              });
            }
          } catch (_) {
            // ignore malformed SSE frame
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Sorry — I couldn't reach the AI just now. Please try again in a moment.",
          };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid={CHAT.toggle}
        onClick={() => onOpenChange(!open)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 px-5 py-3 bg-[color:var(--charcoal)] text-white shadow-xl hover:bg-[color:var(--accent)] transition-colors"
      >
        {open ? <X className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
        <span className="text-sm font-medium">
          {open ? "Close" : "Ask my AI"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT.window}
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-50 bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[440px] md:w-[480px] h-[70vh] max-h-[640px] glass-card flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[color:var(--charcoal)] text-white flex items-center justify-center font-display text-sm">
                  AK
                </div>
                <div>
                  <div className="font-display text-base font-medium leading-tight">
                    Ashwin&rsquo;s AI Agent
                  </div>
                  <div className="text-[11px] font-mono-editorial uppercase tracking-[0.15em] text-[color:var(--muted)] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    Online
                  </div>
                </div>
              </div>
              <button
                data-testid={CHAT.close}
                onClick={() => onOpenChange(false)}
                className="p-1.5 hover:text-[color:var(--accent)] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="chat-scroll flex-1 overflow-y-auto px-5 py-6 space-y-4"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={CHAT.message(i)}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] text-[15px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[color:var(--accent)] text-white px-4 py-2.5"
                        : "text-[color:var(--charcoal)]"
                    } ${
                      streaming && i === messages.length - 1 && m.role === "assistant" && !m.content
                        ? "type-cursor"
                        : ""
                    } ${
                      streaming && i === messages.length - 1 && m.role === "assistant" && m.content
                        ? "type-cursor"
                        : ""
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="pt-2">
                  <div className="text-[11px] font-mono-editorial uppercase tracking-[0.15em] text-[color:var(--muted)] mb-3">
                    Try asking
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        data-testid={CHAT.suggestion(i)}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-1.5 border border-[color:var(--border)] bg-white/60 hover:border-[color:var(--charcoal)] hover:text-[color:var(--accent)] transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/40 p-3 flex items-center gap-2 bg-white/50"
            >
              <input
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about my journey&hellip;"
                disabled={streaming}
                className="flex-1 bg-transparent border-0 outline-none px-3 py-2 text-[15px] placeholder:text-[color:var(--muted)]"
              />
              <button
                data-testid={CHAT.send}
                type="submit"
                disabled={streaming || !input.trim()}
                className="p-2.5 bg-[color:var(--charcoal)] text-white hover:bg-[color:var(--accent)] transition-colors disabled:opacity-40"
              >
                {streaming ? (
                  <MessageCircle className="w-4 h-4 animate-pulse" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default ChatWidget;
